import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Book } from "lucide-react";

export default function OrderBook() {
  const [providerInfo, setProviderInfo] = useState(null);
  const [snap, setSnap] = useState(null);
  const [symbol, setSymbol] = useState("EURUSD");
  const [trades, setTrades] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [pi, s, t] = await Promise.all([
        api.l2ProviderInfo(),
        api.l2Snapshot(symbol),
        api.l2RecentTrades(symbol, 20),
      ]);
      setProviderInfo(pi);
      setSnap(s);
      setTrades(t.trades || []);
    } catch (e) { toast.error("Erreur L2"); }
  };

  useEffect(() => { load(); }, [symbol]);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [running, symbol]);

  const maxBidSize = Math.max(...(snap?.bids || []).map((b) => b.size), 1);
  const maxAskSize = Math.max(...(snap?.asks || []).map((a) => a.size), 1);

  return (
    <div className="p-6 space-y-6" data-testid="orderbook-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Level-2 Order Book</h1>
          <p className="text-white/50 text-sm mt-1">10 niveaux bid/ask + OFI Cont-Kukanov-Stoikov + micro-price Stoikov.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="label">Symbole</label>
            <select className="field w-40" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="ob-symbol">
              {["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "BTCUSD"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <button className={`btn ${running ? "btn-primary" : ""}`} onClick={() => setRunning(!running)} data-testid="ob-live-toggle">
            {running ? "Live ●" : "Statique"}
          </button>
          <button className="btn" onClick={load} data-testid="ob-refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      {providerInfo && (
        <div className="panel p-3 text-xs text-white/70" data-testid="ob-provider-info">
          <b>Provider :</b> {providerInfo.provider} · <b>Source :</b> {providerInfo.source} · {providerInfo.notes}
        </div>
      )}

      {/* Top of book stats */}
      {snap && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Mid" value={snap.mid?.toFixed(5)} />
          <StatBox label="Spread" value={`${snap.spread_bps} bps`} />
          <StatBox label="Imbalance" value={snap.imbalance} highlight={Math.abs(snap.imbalance) > 0.3} />
          <StatBox label="OFI" value={snap.ofi} highlight={Math.abs(snap.ofi) > 50} />
        </div>
      )}

      {/* Book ladder */}
      {snap && (
        <div className="panel p-5">
          <div className="panel-title mb-4"><Book size={14} className="inline mr-2" />Carnet d'ordres — 10 niveaux</div>
          <div className="grid grid-cols-2 gap-4 font-mono text-xs" data-testid="ob-ladder">
            {/* Bids */}
            <div>
              <div className="text-up text-[10px] uppercase tracking-widest mb-2">Bids (acheteurs)</div>
              {snap.bids.map((b, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <div className="w-20 text-right">{b.size}</div>
                  <div className="flex-1 h-4 bg-up/10 relative">
                    <div className="absolute inset-y-0 right-0 bg-up/40" style={{ width: `${(b.size / maxBidSize) * 100}%` }} />
                  </div>
                  <div className="w-20 text-right text-up">{b.price.toFixed(5)}</div>
                </div>
              ))}
            </div>
            {/* Asks */}
            <div>
              <div className="text-down text-[10px] uppercase tracking-widest mb-2">Asks (vendeurs)</div>
              {snap.asks.map((a, i) => (
                <div key={i} className="flex items-center gap-2 py-1">
                  <div className="w-20 text-left text-down">{a.price.toFixed(5)}</div>
                  <div className="flex-1 h-4 bg-down/10 relative">
                    <div className="absolute inset-y-0 left-0 bg-down/40" style={{ width: `${(a.size / maxAskSize) * 100}%` }} />
                  </div>
                  <div className="w-20 text-left">{a.size}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 text-xs text-white/60">
            Micro-price (Stoikov) : <b className="text-accent-blue">{snap.micro_price?.toFixed(5)}</b>
            {" · "}Cumulative OFI : <b>{snap.cumulative_ofi}</b>
            {" · "}Depth bid/ask : {snap.bid_depth_total} / {snap.ask_depth_total}
          </div>
        </div>
      )}

      {/* Recent trades */}
      <div className="panel p-5">
        <div className="panel-title mb-3">Recent Trades ({trades.length})</div>
        {trades.length === 0 ? (
          <div className="text-white/40 text-sm">Aucun trade récent (lance le mode Live).</div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs font-mono" data-testid="ob-trades">
              <thead><tr className="text-white/50">
                <th className="text-left py-1">Side</th>
                <th className="text-right">Prix</th>
                <th className="text-right">Size</th>
                <th className="text-right">Seq</th>
              </tr></thead>
              <tbody>
                {trades.slice().reverse().map((t, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className={t.side === "BUY" ? "text-up" : "text-down"}>{t.side}</td>
                    <td className="text-right">{t.price.toFixed(5)}</td>
                    <td className="text-right">{t.size}</td>
                    <td className="text-right text-white/40">{t.seq}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded ${highlight ? "bg-warn/10 border border-warn/30" : "bg-white/5"}`}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono text-xl mt-1 font-bold ${highlight ? "text-warn" : ""}`}>{value}</div>
    </div>
  );
}
